import { gql } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { Avatar } from 'components/common/AvatarGroup';
import { Timestamp } from 'components/common/Timestamp';
import { typography } from 'components/common/Typography/styles';
import { IconBoldHeart } from 'components/icons/components/bold/IconBoldHeart';
import { IconBoldMessage } from 'components/icons/components/bold/IconBoldMessage';
import { SentimentOptions } from 'features/radarEvent/constants';
import { ExpandableText } from 'features/socialMediaListening';
import { SocialPostCardView } from 'features/socialPost';
import {
  Sentiment,
  SocialPostFragmentSocialPostCardViewFragmentDoc,
} from 'graphql/generated';
import moment from 'moment';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';

export const RADAR_EVENT_FRAGMENT_RADAR_EVENT_TIMELINE_ITEM_SOCIAL_POST_COMMENT = gql`
  fragment RadarEventFragmentRadarEventTimelineItemSocialPostComment on RadarEventModel {
    id
    brandId
    socialPost {
      id
      ...SocialPostFragmentSocialPostCardView
    }
    socialPostComment {
      id
      text
      diggCount
      childCommentCount
      socialPostCommentClaims {
        sentimentScore
        brandId
      }
    }
  }
  ${SocialPostFragmentSocialPostCardViewFragmentDoc}
`;

export type RadarEventTimelineItemSocialPostCommentProps = {
  radarEvent: any;
};

export const RadarEventTimelineItemSocialPostComment = (
  props: RadarEventTimelineItemSocialPostCommentProps,
) => {
  const { radarEvent } = props;

  const socialPost = radarEvent.socialPost;
  const socialPostComment = radarEvent.socialPostComment;

  const claim = socialPostComment.socialPostCommentClaims.find(
    (claim) => claim.brandId === radarEvent.brandId,
  );
  const claimSentiment =
    claim?.sentimentScore > 0
      ? Sentiment.Positive
      : claim?.sentimentScore < 0
      ? Sentiment.Negative
      : Sentiment.Neutral;

  return (
    <Box
      sx={{
        bgcolor: theme.colors?.utility[250],
        p: theme.spacing(7),
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Avatar size={32} user={socialPostComment.commentCreator} />
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="headline-sm">
              {socialPostComment.commentCreator.handle}
            </Typography>{' '}
            <Typography variant="subhead-lg" color={theme.colors?.utility[700]}>
              <Timestamp
                date={moment(radarEvent.platformCreateTime)}
                humanized={false}
                format="MMM DD, YYYY • hh:mm A"
              />
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Box
            sx={{
              ...SentimentOptions[claimSentiment]?.sx,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: theme.spacing(1, 3),
              borderRadius: 5,
            }}
          >
            <Typography
              variant="body-xl"
              fontWeight={600}
              color={theme.colors?.utility[800]}
            >
              {SentimentOptions[claimSentiment]?.label}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: 'rgba(35, 6, 3, 0.05)',
              p: theme.spacing(1, 3),
              borderRadius: 5,
            }}
          >
            <IconBoldMessage size={16} color={theme.colors?.utility[600]} />
            <Typography
              variant="body-xl"
              fontWeight={600}
              color={theme.colors?.utility[800]}
            >
              Brand Engagement
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          pt: theme.spacing(5),
          pr: theme.spacing(5),
          borderRadius: 3,
          display: 'flex',
          gap: 4,
        }}
      >
        <Box>
          <Box
            sx={{
              width: 140,
            }}
          >
            <SocialPostCardView socialPost={socialPost} />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            flex: 1,
          }}
        >
          <ExpandableText
            collapsedHeight={100}
            text={socialPostComment.text}
            componentProps={{
              text: {
                sx: {
                  ...typography['subhead-xl'],
                },
              },
              seeMore: {
                label: 'See more',
              },
              seeLess: {
                label: 'Hide',
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
              mt: 'auto',
            }}
          >
            {[
              ...(socialPostComment.diggCount >= 0
                ? [
                    [
                      IconBoldHeart,
                      formatBigNumber(socialPostComment.diggCount || 0),
                    ],
                  ]
                : []),
              ...(socialPostComment.childCommentCount >= 0
                ? [
                    [
                      IconBoldMessage,
                      formatBigNumber(socialPostComment.childCommentCount || 0),
                    ],
                  ]
                : []),
            ].map(([Icon, text], index) => {
              return (
                <Box
                  key={index}
                  display="flex"
                  gap={1}
                  alignItems="center"
                  sx={{
                    backgroundColor: 'rgba(35, 6, 3, 0.05)',
                    p: theme.spacing(1, 3),
                    borderRadius: 5,
                  }}
                >
                  <Icon size={16} color={theme.colors?.utility[600]} />
                  <Typography
                    color={theme.colors?.utility[800]}
                    variant="headline-xs"
                  >
                    {String(text)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
